<template>
  <div :class="{ 'has-no-before-integer-slot': hasNoBeforeIntegerSlot }" class="decimal-input">
    <div class="integer">
      <div class="input-group">
        <slot name="before-integer" class="input-group-addon" />
        <input
          v-bind="$attrs"
          ref="integerInput"
          :data-test="dataTestPrefix + 'integerInput'"
          :value="integerLocal"
          :readonly="readOnly"
          :min="minimumValueObject.integer"
          :max="max"
          class="form-control text-right input-integer"
          :class="{ 'input-error': !!error }"
          type="number"
          step="1"
          name="amount_integer"
          @input="onIntegerInput"
          @blur="onIntegerBlur"
        />
      </div>
    </div>
    <div class="decimal pull-left f-17 f-500 text-center p-t-5">
      {{ decimalPoint }}
    </div>
    <div :class="{ 'fraction-wide': fractionDecimals === 3 }" class="fraction">
      <input
        v-bind="$attrs"
        ref="fractionInput"
        :data-test="dataTestPrefix + 'fractionInput'"
        :value="fractionLocal"
        :readonly="readOnly"
        :min="minimumValueObject.fraction"
        :max="fractionMax"
        :placeholder="fractionPlaceholder"
        class="form-control input-fraction"
        :class="{ 'input-error': !!error }"
        type="number"
        name="amount_fraction"
        @input="onFractionInput"
        @blur="onFractionBlur"
        @focus="$event.target.select()"
      />
    </div>
    <input v-if="$attrs.name" :name="$attrs.name" :value="formattedValue" type="hidden" />
    <div class="clear" />
  </div>
  <div v-if="error && error.length" class="tw-text-red-500 tw-mt-1">
    <span>{{ error }}</span>
  </div>
</template>

<script>
import { getRateAsIntegerAndFraction } from '@/components/utilities/numberFormatting'

export const validateFractionDecimals = (value) => [2, 3].includes(value)

export default {
  props: {
    modelValue: {
      type: Object,
      default: () => ({ integer: 0, fraction: '0' }),
    },
    formatted: {
      type: Number,
      default: 0,
    },
    decimalPoint: {
      type: String,
      default: this?.account?.locale === 'da' ? ',' : '.',
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: null,
    },
    fractionMin: {
      type: Number,
      default: 0,
    },
    fractionMax: {
      type: Number,
      default: null,
    },
    fractionPlaceholder: {
      type: [Number, String],
      default: null,
    },
    fractionDecimals: {
      type: Number,
      default: 2,
      validator: validateFractionDecimals,
    },
    error: {
      type: [Boolean, String],
      default: null,
    },
    dataTestPrefix: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'update:formatted', 'blur'],
  data() {
    const { integer, fraction } =
      this.formatted > 0
        ? this.convertToObject(this.formatted)
        : (this.modelValue ?? { integer: 0, fraction: '0' })

    return {
      integerLocal: integer,
      fractionLocal: this.formatFraction(fraction),
      isTyping: false,
    }
  },
  computed: {
    formattedValue() {
      return parseFloat(`${this.integerLocal ?? 0}.${this.fractionLocal ?? 0}`)
    },
    minimumValueObject() {
      const current = getRateAsIntegerAndFraction(this.formattedValue)
      const minimum = getRateAsIntegerAndFraction(this.min)

      // if the main integer is lower than the minimum, we do not care about validating a minimum fraction
      // if the main integer is higher than the minimum, we do not care about validating a minimum fraction
      // if the main integer equals the minimum, we must validate a minimum fraction
      const fraction =
        minimum.integer === current.integer ? Math.max(minimum.fraction, this.fractionMin) : 0

      return {
        integer: minimum.integer,
        fraction,
      }
    },
    hasNoBeforeIntegerSlot() {
      return !this.$slots['before-integer']
    },
  },
  watch: {
    modelValue: {
      deep: true,
      handler(newVal) {
        if (this.isTyping || !newVal) {
          return
        }

        if (newVal.integer !== this.integerLocal || newVal.fraction !== this.fractionLocal) {
          this.integerLocal = newVal.integer
          this.fractionLocal = this.formatFraction(newVal.fraction)
        }
      },
    },
  },
  methods: {
    emitUpdate(emitFormatted = true) {
      const value = {
        integer: this.integerLocal,
        fraction: this.fractionLocal,
      }

      this.$emit('update:modelValue', value)

      if (emitFormatted) {
        this.$emit('update:formatted', this.formattedValue)
      }
    },
    onIntegerInput(e) {
      this.isTyping = true

      const { value } = e.target

      this.integerLocal = value === '' ? null : parseInt(value, 10)

      this.emitUpdate(false)
    },
    onIntegerBlur(e) {
      if (this.readOnly) {
        return
      }

      this.isTyping = false

      this.integerLocal = parseInt(e.target.value || 0, 10)

      if (this.max !== null && this.integerLocal >= this.max) {
        this.integerLocal = this.max
        this.fractionLocal = '0'.repeat(this.fractionDecimals)
      }

      this.emitUpdate(true)

      this.$emit('blur')
    },
    onFractionInput(e) {
      this.isTyping = true

      const { value } = e.target

      this.fractionLocal = this.formatFraction(value ?? '')

      this.emitUpdate(true)
    },
    onFractionBlur(e) {
      if (this.readOnly) {
        return
      }

      this.isTyping = false

      let value = e.target.value || '0'

      value = this.formatFractionOnBlur(value)

      const num = parseInt(value, 10)

      if (num < this.fractionMin) {
        value = String(this.fractionMin).padStart(this.fractionDecimals, '0')
      }

      if (this.max !== null && this.integerLocal >= this.max) {
        value = '0'.repeat(this.fractionDecimals)
      }

      this.fractionLocal = value

      this.emitUpdate(true)

      this.$emit('blur')
    },
    formatFraction(value) {
      const str = String(value ?? '')

      return str.length > this.fractionDecimals ? str.slice(-this.fractionDecimals) : str
    },
    formatFractionOnBlur(val) {
      const v = String(val || '0')

      if (/^0+$/.test(v)) {
        return '0'.repeat(this.fractionDecimals)
      }

      if (v.length < this.fractionDecimals) {
        return v.padEnd(this.fractionDecimals, '0')
      }

      return v.slice(-this.fractionDecimals)
    },
    convertToObject(value) {
      const [integer, fraction = '0'] = String(value).split('.')

      return {
        integer: parseInt(integer, 10),
        fraction: this.formatFraction(fraction),
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import 'resources/sass/frontend/_color-variables';

.decimal-input {
  display: flex;
  flex-wrap: wrap;
}

.input-integer {
  .has-no-before-integer-slot & {
    border-radius: 4px;
  }
}

.integer {
  flex: 1 0 0;
}

.fraction {
  flex: 0;
  flex-basis: 43px;
}

.fraction-wide {
  flex-basis: 48px;
}

.input-error:not(:focus) {
  border-color: $red-500 !important;
}
</style>
